import { CalendarIcon, DownArrowIcon } from "@xola/icons";
import { DatePickerPopover } from "@xola/ui-kit";
import { DateFormat, formatDate, getMonthStartEndRange, getWeekStartEndRange } from "common";
import { Dayjs } from "dayjs";
import React, { useMemo } from "react";
import shallow from "zustand/shallow";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardMode } from "@/types/Dashboard";

interface DashboardDateFilterProps {
    value: Date;
    onChange?: (value: Date) => void;
}

export const DashboardDateFilter = ({ value, onChange }: DashboardDateFilterProps) => {
    const mode = useDashboardFilterStore((state) => state.filters.mode);

    const dateValue = formatDate(value, DateFormat.DATE_MONTH);

    return (
        <div className="inline-flex align-top" data-testid="dashboard-date-picker-button">
            <DatePickerPopover
                shouldShowMonthSelector
                classNames={{ popover: "p-3" }}
                pickerType={mode === DashboardMode.MONTH ? "month" : "day"}
                value={value}
                onChange={onChange}
            >
                <div className="flex h-[30px] cursor-pointer items-center space-x-2 rounded border border-gray-light p-3">
                    <span>
                        <CalendarIcon size="medium" />
                    </span>
                    <span>{dateValue}</span>
                    <span>
                        <DownArrowIcon size="medium" />
                    </span>
                </div>
            </DatePickerPopover>
        </div>
    );
};

export const useDashboardDateFilterRange = () => {
    const { startDate, endDate, mode } = useDashboardFilterStore((state) => {
        return {
            startDate: state.date,
            endDate: state.endDate,
            mode: state.getMode(),
        };
    }, shallow);

    return useMemo(() => {
        const { start, end } = getDaysRangeByMode(mode, startDate, endDate);

        return { start, end };
    }, [mode, startDate, endDate]);
};

/**
 * Returns appropriate start/end based on current mode.
 */
const getDaysRangeByMode = (mode: DashboardMode, start: Dayjs, end: Dayjs) => {
    switch (mode) {
        case DashboardMode.MONTH: {
            return getMonthStartEndRange(start);
        }
        case DashboardMode.WEEK: {
            return getWeekStartEndRange(start);
        }
        default:
            return { start, end };
    }
};

export interface TagTargetProps {
    value: string;
    onClick: () => void;
    showSingleDate: boolean;
}
